import React from 'react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  /** Illustrative icon, rendered above the title (decorative). */
  icon?: React.ReactNode;
  /** Primary line — what the empty state means. */
  title: React.ReactNode;
  /** Optional supporting line — what the user can do about it. */
  description?: React.ReactNode;
  /** Optional call to action (e.g. a "Clear filters" or "Add" button). */
  action?: React.ReactNode;
  className?: string;
}

/**
 * Shared empty-state block for tables and lists: a centered icon, title,
 * description, and optional action. Replaces bare "No results found" strings so
 * an empty table reads as a designed state and can tell the user why it's empty
 * (nothing here yet vs. no matches for the current filters/search).
 *
 * Follows the same centered, role="status" pattern as
 * components/billing/contracts/timeline/EmptyState.tsx.
 */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-4 py-10 text-center',
        className,
      )}
      role="status"
    >
      {icon && (
        <div className="mb-3 text-muted-foreground/60" aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 className="text-base font-medium text-foreground">{title}</h3>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
